import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IBlindTest } from "../../../Models/IBlindTest";
import { IDesignSession } from "../../../Models/IDesignSession";
import { IBlindTestState } from "../../../Models/IBlindTestState";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../Services/ApiService";
import logger from '../../../Services/LoggingService';
import { IMsalContext, useMsal } from "@azure/msal-react";
import { deCapitalizeFirstLetter, getFlowTypeUnits, getPreferredUnits } from "../../../Services/Global";
import { generateHeader } from "../../../Utils/UnitConfigurationHelper";

// Define a column interface
interface Column {
    key: string;
    name: string;
    minWidth?: number;
    maxWidth?: number;
    fieldName?: string;
    onRender?: (item: any, index?: number, column?: Column) => React.ReactNode;
}

/**
 * Blind test details list component for displaying the generated designs for
 * a blind test.
 *
 * Columns are built deterministically inside a single async effect and
 * committed to state as one atomic update (replacing the previous pattern
 * that relied on hoisted `let` mutation across renders). All network calls
 * are wrapped in a try/catch/finally so the loading spinner is always
 * cleared and errors surface through a Snackbar.
 */
export const BlindTestDetailsList: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const ctx: IMsalContext = useMsal();
    const theme = useTheme();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);

    const stateParam = location.state as IBlindTestState;
    const blindtest = stateParam?.blindTest;
    const unitConfig = stateParam?.unitConfiguration;

    const [designSessions, setDesignSessions] = useState<IBlindTest[]>();
    const [dataColumns, setDataColumns] = useState<Column[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const styles = useMemo(() => ({
        container: {
            padding: theme.spacing(2),
            flexGrow: 1,
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: theme.spacing(3),
            gap: theme.spacing(2),
        },
        title: {
            color: theme.palette.primary.main,
            marginLeft: theme.spacing(1),
        },
        tableContainer: {
            marginTop: theme.spacing(2),
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        },
        hotValue: {
            color: theme.palette.error.dark,
        },
        coldValue: {
            color: theme.palette.primary.main,
        }
    }), [theme]);

    useEffect(() => {
        if (!blindtest || !unitConfig) return;

        let cancelled = false;

        const getDesignSessionsForBlindTest = async () => {
            setIsLoading(true);
            try {
                // Run both requests in parallel – they are independent.
                const [sessionsResponse, unitConfigResponse] = await Promise.all([
                    apiService.getAsync(`blindtests/designsessions/${blindtest.id}`),
                    apiService.getAsync(`settings/unitconfiguration`, unitConfig),
                ]);

                if (!sessionsResponse.ok) {
                    throw new Error(`Could not fetch design sessions: ${sessionsResponse.status} ${sessionsResponse.statusText}`);
                }
                if (!unitConfigResponse.ok) {
                    throw new Error(`Could not fetch unit configuration: ${unitConfigResponse.status} ${unitConfigResponse.statusText}`);
                }

                const sessions: IBlindTest[] = await sessionsResponse.json();
                const unitConfigResult: IUnitConfiguration = await unitConfigResponse.json();

                if (cancelled) return;

                const columns = buildColumns(unitConfigResult, styles);

                setDataColumns(columns);
                setDesignSessions(sessions);
            } catch (err) {
                if (cancelled) return;
                const errorMessage = err instanceof Error ? err.message : 'Could not load blind test details';
                setError(errorMessage);
                logger.error("Failed to load blind test details", err as Error, {
                    blindTestId: blindtest.id
                }, {
                    component: 'BlindTestDetailsList',
                    method: 'getDesignSessionsForBlindTest'
                });
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        getDesignSessionsForBlindTest();

        return () => {
            cancelled = true;
        };
    }, [blindtest, unitConfig, apiService, styles]);

    const renderCellContent = (value: any): React.ReactNode => {
        if (value === null || value === undefined) {
            return '';
        }
        if (Array.isArray(value)) {
            return value.join(', ');
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const renderTableRows = () => {
        if (!designSessions || !dataColumns) return null;

        return designSessions.map((session, rowIndex) => (
            <TableRow key={`row-${rowIndex}`}>
                {dataColumns.map((column, colIndex) => (
                    <TableCell
                        key={`cell-${rowIndex}-${colIndex}`}
                        style={{
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth
                        }}
                    >
                        {column.onRender
                            ? column.onRender(session, rowIndex, column)
                            : renderCellContent(session[column.fieldName as keyof typeof session])}
                    </TableCell>
                ))}
            </TableRow>
        ));
    };

    if (!blindtest || !unitConfig) {
        return (
            <Box sx={styles.container}>
                <Typography variant="h6">No blind test data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={styles.container}>
            <Box sx={styles.header}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
                <Typography variant="h6" sx={styles.title}>
                    Generated designs for Blind test: {blindtest.blindTestSetId}
                </Typography>
            </Box>

            {isLoading ? (
                <Box sx={styles.loadingContainer}>
                    <CircularProgress />
                </Box>
            ) : designSessions && dataColumns ? (
                <TableContainer component={Paper} sx={styles.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {dataColumns.map((column, index) => (
                                    <TableCell
                                        key={`header-${index}`}
                                        style={{
                                            minWidth: column.minWidth,
                                            maxWidth: column.maxWidth,
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {column.name}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderTableRows()}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body1">No data available</Typography>
            )}

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" variant="filled">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

/**
 * Builds the full column list for a given unit configuration.
 *
 * Extracted as a pure helper so the async effect has a deterministic,
 * single-assignment column set (replacing the previous hoisted `let` mutation
 * pattern that was both order-dependent and error-prone on re-renders).
 *
 * Unit preferences are resolved once here and captured by the `onRender`
 * closures, so subsequent re-renders always display the values that match
 * the backend unit configuration we just fetched.
 */
const buildColumns = (
    unitConfigResult: IUnitConfiguration,
    styles: { hotValue: object; coldValue: object }
): Column[] => {
    const preferredTemperatureUnits = `°${getPreferredUnits().preferredUnits === 'us' ? 'F' : 'C'}`;
    const preferredPressureUnits = getPreferredUnits().preferredUnits === 'us' ? 'PSI' : 'kPa';
    const preferredFlowrateUnits = getFlowTypeUnits(unitConfigResult.flowType!);

    const baseColumns: Column[] = [
        {
            key: 'ID',
            name: 'Model ID',
            minWidth: 100,
            maxWidth: 150,
            fieldName: 'modelId',
            onRender: (item?: IDesignSession, index?: number) => (
                <Tooltip title={item?.modelId || ''} key={`tooltip-${index}-modelid`}>
                    <Typography variant="body2">{item?.modelId}</Typography>
                </Tooltip>
            )
        },
        {
            key: 'HotMediaType',
            name: 'Hot media type',
            minWidth: 80,
            maxWidth: 120,
            fieldName: 'hotMediaType',
            onRender: (input) => (
                <Tooltip title={input.hotMediaType || ''}>
                    <Typography variant="body2">{input.hotMediaType}</Typography>
                </Tooltip>
            )
        },
        {
            key: 'ColdMediaType',
            name: 'Cold media type',
            minWidth: 80,
            maxWidth: 120,
            fieldName: 'coldMediaType',
            onRender: (input) => (
                <Tooltip title={input.coldMediaType || ''}>
                    <Typography variant="body2">{input.coldMediaType}</Typography>
                </Tooltip>
            )
        }
    ];

    const dynamicColumns: Column[] = unitConfigResult.inputs.map((input: string) => {
        const key = deCapitalizeFirstLetter(input);
        const name = generateHeader(input, unitConfigResult);
        const lowerKey = key.toLowerCase();
        // Resolve unit suffix once per column instead of per-cell render.
        const unit = lowerKey.includes('flowrate')
            ? preferredFlowrateUnits
            : lowerKey.includes('pressure')
                ? preferredPressureUnits
                : lowerKey.includes('temp')
                    ? preferredTemperatureUnits
                    : '';

        return {
            key,
            name,
            minWidth: 100,
            maxWidth: 150,
            fieldName: key,
            onRender: (item?: any, index?: number, column?: Column) => {
                const raw = item?.[column!.fieldName!];
                const output = raw ? `${raw} ${unit}`.trim() : '';
                return (
                    <Tooltip title={output} key={`tooltip-${index}-${column?.key}`}>
                        <Typography
                            variant="body2"
                            sx={column!.fieldName!.toLowerCase().includes('hot') ? styles.hotValue : styles.coldValue}
                        >
                            {output}
                        </Typography>
                    </Tooltip>
                );
            }
        };
    });

    const trailingColumn: Column = {
        key: '1',
        name: '',
        minWidth: 100,
        maxWidth: 100,
        fieldName: 'render',
    };

    return [...baseColumns, ...dynamicColumns, trailingColumn];
};
